import { useAppSelector } from '../../../../../../../../app/hooks'
import { RentControlState, RentIndexState, ValuationState } from '../../../../../../../../@types/state-types'
import { InitState } from '../../../../../../../../redux/reducers/root/initialState'
import { calculateRentControlMaxValue, getCurrentRentControlApplies } from '../rent-control-benchmark'

export type PriceBenchmarkSource = 'askingRent' | 'rentIndex'

export type PriceBenchmarkData = {
  benchmarkSource: PriceBenchmarkSource
  benchmarkMinValue?: number
  benchmarkMaxValue?: number
  benchmarkRecommendedValue?: number
  benchmarkRentControlMaxValue?: number
}

type RentIndexBenchmarkState = RentIndexState & {
  status: 'available'
  minValue: number
  averageValue: number
  maxValue: number
}

export const hasRentIndexBenchmarkData = (
  rentIndexResult: RentIndexState
): rentIndexResult is RentIndexBenchmarkState =>
  rentIndexResult.status === 'available' &&
  rentIndexResult.minValue !== undefined &&
  rentIndexResult.averageValue !== undefined &&
  rentIndexResult.maxValue !== undefined

export function getPriceBenchmarkData(
  valuationFeature: ValuationState,
  rentIndexResult: RentIndexState,
  rentControlResult: RentControlState,
  currentCity?: string
): PriceBenchmarkData {
  const { minValue, maxValue, recommendedValue } = valuationFeature
  const rentControlApplies = getCurrentRentControlApplies(rentControlResult, currentCity)

  if (hasRentIndexBenchmarkData(rentIndexResult)) {
    const {
      minValue: rentIndexMinValue,
      averageValue: rentIndexAverageValue,
      maxValue: rentIndexMaxValue,
    } = rentIndexResult

    return {
      benchmarkSource: 'rentIndex',
      benchmarkMinValue: rentIndexMinValue,
      benchmarkMaxValue: rentIndexMaxValue,
      benchmarkRecommendedValue: rentIndexAverageValue,
      benchmarkRentControlMaxValue:
        rentControlApplies === true ? calculateRentControlMaxValue(rentIndexMaxValue) : undefined,
    }
  }

  return {
    benchmarkSource: 'askingRent',
    benchmarkMinValue: minValue,
    benchmarkMaxValue: maxValue,
    benchmarkRecommendedValue: recommendedValue,
  }
}

export function usePriceBenchmarkData(): PriceBenchmarkData {
  const valuationFeature = useAppSelector((state: InitState) => state.valuationFeature)
  const rentIndexResult = useAppSelector((state: InitState) => state.rentIndexFeature)
  const rentControlResult = useAppSelector((state: InitState) => state.rentControlFeature)
  const currentCity = useAppSelector((state: InitState) => state.insertionFeature.insertionData.city)

  return getPriceBenchmarkData(valuationFeature, rentIndexResult, rentControlResult, currentCity)
}
